import { useMemo } from 'react'
import { Box, BoxProps, Container } from '@mui/material'
import Image from 'next/image'

import { useEmblaCarouselHook } from '@/hooks'

import EmblaControls from '../base/embla-controls'
import { WPImage } from '@/types'

export type GalleryProps = BoxProps &
  Partial<{
    items: WPImage[]
    variant: 'card' | 'full'
  }>

const Gallery = ({ items = [], variant = 'card', ...props }: GalleryProps) => {
  const {
    emblaRef,
    emblaApi,
    scrollSnaps,
    selectedIndex,
    nextBtnDisabled,
    prevBtnDisabled,
    scrollNext,
    scrollPrev
  } = useEmblaCarouselHook({
    align: 'start',
    skipSnaps: false,
    active: items.length > 1,
    breakpoints:
      variant === 'card'
        ? {
            '(min-width: 768px)': { active: items.length > 2 },
            '(min-width: 1024px)': { active: items.length > 3 }
          }
        : undefined
  })

  const needsControls = useMemo(
    () => !(nextBtnDisabled && prevBtnDisabled),
    [nextBtnDisabled, prevBtnDisabled]
  )

  return (
    <Box {...props}>
      <Container>
        <Box
          ref={emblaRef}
          sx={{ overflow: 'hidden', borderRadius: (theme) => theme.shape.borderRadius7 }}
        >
          <Box
            sx={{
              display: 'flex',
              marginLeft: { xs: -2, sm: -3, md: -4 },
              touchAction: 'pan-y pinch-zoom'
            }}
          >
            {items.map((item, index) => (
              <Box
                key={`item-${index}`}
                sx={{
                  minWidth: 0,
                  flexShrink: 0,
                  flexGrow: 0,
                  flexBasis:
                    variant === 'card'
                      ? { xs: `${100}%`, sm: `${(1 / 2) * 100}%`, md: `${(1 / 3) * 100}%` }
                      : '100%',
                  paddingLeft: { xs: 2, sm: 3, md: 4 }
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    aspectRatio: variant === 'card' ? '1/1' : '16/9',
                    overflow: 'hidden',
                    borderRadius: (theme) => theme.shape.borderRadius6,
                    ['&>img']: {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }
                  }}
                >
                  <Image
                    src={item?.url}
                    alt={item?.alt || ''}
                    width={item?.width || 800}
                    height={item?.height || 450}
                    draggable={false}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {needsControls ? (
          <EmblaControls
            sx={{ mt: 4 }}
            scrollSnaps={scrollSnaps}
            selectedIndex={selectedIndex}
            prevBtnDisabled={prevBtnDisabled}
            nextBtnDisabled={nextBtnDisabled}
            onDotClick={(index) => emblaApi?.scrollTo(index)}
            scrollPrev={scrollPrev}
            scrollNext={scrollNext}
          />
        ) : null}
      </Container>
    </Box>
  )
}

export default Gallery
